<script>
    import Fa from 'svelte-fa';
    import { faChevronRight, faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

    import { onDestroy, onMount } from 'svelte';
    import { browser } from '$app/environment';

    import Picture from '$lib/components/Picture.svelte';

    export let images;
    export let selectedVariant;
    export let classes = '';
    export let title = '';

    let selectedIndicator = '';
    let thumbContainer;
    let galleryContainer;

    // Flter our the first image (Removes Featured Image) in the array if there are more than 2 images
    images = images.filter((image, index) => !(images.length > 2 && index === 0));

    // Scroll image into view inside containter
    const scrollImageIntoView = (id) => {
        const image = document.getElementById(id);

        if (!image) return;

        // Calculate the position to scroll to
        const scrollPosition = image.offsetLeft - galleryContainer.offsetWidth / 2 + image.offsetWidth / 2;

        // Scroll the container
        galleryContainer.scrollLeft = scrollPosition;
    };

    // Scroll thumb container left or right on nav clicks
    const scrollThumbContainer = (direction) => {
        const scrollDistance = 296.5;
        if (direction === 'right') {
            thumbContainer.scrollLeft += scrollDistance;
        } else {
            thumbContainer.scrollLeft -= scrollDistance;
        }
    };

    // Scroll selected image into view on selectedVariant change
    $: if (selectedVariant && browser && images.length > 1) {
        scrollImageIntoView(selectedVariant?.image?.url);
    }

    // Intersection Observer to track which image is in view
    let observer;
    let imageContainers = [];
    const options = { root: null, threshold: 0.5 };

    onDestroy(() => {
        if (observer) {
            observer.disconnect();
        }
    });

    onMount(() => {
        if (images.length <= 1) return;

        import('$lib/utils/polyfill/IntersectionObserver').then(() => {
            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        selectedIndicator = entry.target.id;
                    }
                });
            }, options);

            imageContainers.forEach((imageContainer) => {
                observer.observe(imageContainer);
            });
        });
    });
</script>

<div class={classes}>
    <div class="relative">
        <div class="no-scrollbar relative flex snap-x snap-mandatory overflow-x-auto scroll-smooth will-change-scroll" bind:this={galleryContainer}>
            {#each images as image, index (image)}
                <div class="min-w-fit max-w-fit flex-none snap-center snap-always scroll-m-0" id={image?.node?.url} bind:this={imageContainers[index]}>
                    <Picture
                        src={image?.node?.url}
                        alt={image?.node?.altText || title}
                        width="650"
                        height="650"
                        imageSizes={[320, 490, 600]}
                        lazy={index > 0}
                        lcp={index === 0}
                    />
                </div>
            {/each}
        </div>

        {#if images.length > 1}
            <div class="absolute bottom-0 flex w-full items-center justify-center lg:bottom-4">
                {#each images as image, index (image)}
                    <button
                        class={`mr-2 inline-block h-2 w-2 rounded-full duration-300 ease-in-out ${selectedIndicator === image?.node?.url ? 'bg-primary' : 'bg-base-700'}`}
                        on:click={() => scrollImageIntoView(image?.node?.url)}
                        aria-label={`View image ${index + 1}`}
                    ></button>
                {/each}
            </div>
        {/if}
    </div>

    {#if images.length > 1}
        <div class="relative hidden overflow-hidden pt-2 lg:flex">
            <div
                class="no-scrollbar flex w-full snap-x snap-mandatory items-center overflow-x-auto scroll-smooth will-change-scroll"
                class:justify-center={images.length <= 4}
                bind:this={thumbContainer}
            >
                {#if images.length > 4}
                    <button
                        class="absolute left-0 z-20 h-full rounded-tr-lg px-3 transition-all duration-200 ease-in-out hover:bg-base-800/80"
                        on:click={() => scrollThumbContainer('left')}
                        aria-label="Scroll Left"><Fa icon={faChevronLeft} class="inline-block" /></button
                    >
                {/if}
                {#each images as image, index (image)}
                    <div class="w-1/4 flex-none text-center">
                        <button on:click={() => scrollImageIntoView(image?.node?.url)} aria-label="Thumbnail">
                            <Picture
                                src={image?.node?.url}
                                alt={image?.node?.altText || title}
                                width="120"
                                height="120"
                                imageSizes={[120, 120, 160]}
                                lazy={index > 4}
                            />
                        </button>
                    </div>
                {/each}
                {#if images.length > 4}
                    <button
                        class="absolute right-0 z-20 h-full rounded-tl-lg px-3 transition-all duration-200 ease-in-out hover:bg-base-800/80"
                        on:click={() => scrollThumbContainer('right')}
                        aria-label="Scroll Right"><Fa icon={faChevronRight} class="inline-block" /></button
                    >
                {/if}
            </div>
        </div>
    {/if}
</div>
